import Loader from '@components/framework/loader/Loader';
import {FormField} from '@components/system/FormField/FormField';
import {
  IProcedure,
  IProcedureInputField
} from '@shared/services/service-mappers/SmartCoderServiceMapper';
import {
  getAllProcedures,
  getProcedureInputFields
} from '@shared/services/SmartCoderServices';
import React, {useEffect, useState} from 'react';
import {Controller, UseFormMethods} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import ProcedureInputFields from '../../ProcedureInputFields/ProcedureInputFields';
import {SMART_CODER_PROCEDURE_TYPES} from '@shared/constants/Mapping';

interface IGenerateProcedureCPTCodeTab {
  clinicGroupId: number;
  clinicId: number;
  relatedId: number;
  relatedType: string;
  useFormMethods: UseFormMethods;
  updateSubmitButtonState: (arg: {
    isDisabled: boolean;
    isLoading: boolean;
  }) => void;
  defaultValues?: Record<string, any>;
  clearGeneratedCPTCode?: () => void;
}
const GenerateProcedureCPTCodeTab = (
  props: IGenerateProcedureCPTCodeTab
): JSX.Element => {
  const {
    clinicGroupId,
    clinicId,
    relatedId,
    relatedType,
    useFormMethods,
    updateSubmitButtonState,
    defaultValues,
    clearGeneratedCPTCode
  } = props;

  const {t} = useTranslation();
  const {procedure_code_id: procedureId} = useFormMethods.watch([
    'procedure_code_id'
  ]);

  const [procedures, setProcedures] = useState({
    isLoading: false,
    data: [] as IProcedure[]
  });

  const [procedureFields, setProcedureFields] = useState({
    isLoading: false,
    data: [] as IProcedureInputField[]
  });

  useEffect(() => {
    const curFormValues = useFormMethods.getValues();
    for (const formKey in curFormValues) {
      if (
        !['procedure_code_id', 'doctors_notes', 'sedation'].includes(formKey)
      ) {
        useFormMethods.setValue(formKey, null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedureId]);

  useEffect(() => {
    if (procedures.isLoading || procedureFields.isLoading) {
      updateSubmitButtonState({
        isDisabled: true,
        isLoading: true
      });
    } else {
      updateSubmitButtonState({
        isDisabled: false,
        isLoading: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedures.isLoading, procedureFields.isLoading]);

  useEffect(() => {
    const fetchProcedures = async () => {
      setProcedures(p => ({
        ...p,
        isLoading: true
      }));
      try {
        const procedureRes = await getAllProcedures({
          clinicGroupId,
          clinicId,
          params: {
            type: SMART_CODER_PROCEDURE_TYPES.PAIN_MED_PROCEDURE
          }
        });
        if (Array.isArray(procedureRes.data)) {
          setProcedures({
            data: procedureRes.data,
            isLoading: false
          });
        } else {
          setProcedures({
            data: [],
            isLoading: false
          });
        }
      } catch (err) {
        setProcedures({
          data: [],
          isLoading: false
        });
      }
    };
    fetchProcedures();
  }, [clinicGroupId, clinicId, relatedId, relatedType]);

  useEffect(() => {
    const fetchProcedureFields = async () => {
      try {
        setProcedureFields(p => ({
          ...p,
          isLoading: true
        }));

        const fieldsRes = await getProcedureInputFields({
          clinicGroupId: clinicGroupId,
          clinicId: clinicId,
          procedureId: procedureId
        });
        if (Array.isArray(fieldsRes.data)) {
          setProcedureFields({
            data: fieldsRes.data,
            isLoading: false
          });
        } else {
          setProcedureFields({
            data: [],
            isLoading: false
          });
        }
      } catch (err) {
        setProcedureFields({
          data: [],
          isLoading: false
        });
      }
    };
    if (procedureId) {
      fetchProcedureFields();
    } else {
      setProcedureFields({
        data: [],
        isLoading: false
      });
    }
  }, [procedureId, clinicGroupId, clinicId]);

  const shouldShowLoader = procedures.isLoading || procedureFields.isLoading;

  const selectedProcedure = procedures.data.find(p => p.id == procedureId);

  return (
    <div className="procedure-fields-wrapper">
      <div className="procedure-left-fields-wrapper">
        <Controller
          control={useFormMethods.control}
          name={'procedure_code_id'}
          defaultValue={
            defaultValues?.['procedure_code_id']
              ? String(defaultValues?.['procedure_code_id'])
              : null
          }
          rules={{
            validate: value => {
              if (!value) {
                return t(
                  'SMART_CODER.GENERATE_CPT_CODES_DIALOG.VALIDATION_ERROR_FIELD_REQUIRED',
                  {
                    field: t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.PROCEDURE_FIELD_LABEL'
                    ) as string
                  }
                ) as string;
              }
              return true;
            }
          }}
          render={inputProps => {
            return (
              <FormField
                label={t(
                  'SMART_CODER.GENERATE_CPT_CODES_DIALOG.PROCEDURE_FIELD_LABEL'
                )}
                type={'radio'}
                placeholder={t(
                  'SMART_CODER.GENERATE_CPT_CODES_DIALOG.PROCEDURE_FILED_PLACEHOLDER'
                )}
                options={procedures.data.map(p => ({
                  label: p.label,
                  value: String(p.id)
                }))}
                required={true}
                {...inputProps}
                error={useFormMethods.errors['procedure_code_id']}
              />
            );
          }}
        />
      </div>
      <div className="procedure-middle-fields-wrapper">
        {shouldShowLoader && <Loader />}
        {!shouldShowLoader && (
          <ProcedureInputFields
            key={procedureId}
            fields={procedureFields.data}
            defaultValues={defaultValues}
            procedureId={procedureId}
            useFormMethods={useFormMethods}
            onInputValueChange={() => {
              clearGeneratedCPTCode();
            }}
            clearFieldValue={fieldName => {
              useFormMethods.setValue(fieldName, null);
              clearGeneratedCPTCode();
            }}
            procedureName={selectedProcedure?.label}
          />
        )}
        {!procedureId && !shouldShowLoader && (
          <div>
            {t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.SELECT_PROCEDURE_LABEL')}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateProcedureCPTCodeTab;
